import React, { useEffect, useState } from "react";
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Button,
  IconButton,
  Box,
  FormHelperText,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { productService } from "../../services/product-service";
import ProductSelector from "../../components/product-selector";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CustomerAutocomplete from "../../components/customer-autocomplete";
import { orderService } from "../../services/order-service";
import AddressLocationFields from "../../components/address-location";

const initialAddress = {
  first_name: "",
  last_name: "",
  address1: "",
  address2: "",
  company: "",
  city: "",
  zip: "",
  province: "",
  province_code: "",
  country: "",
  country_code: "",
  phone: "",
};

const fieldsToRender = {
  first_name: "",
  last_name: "",
  address1: "",
  address2: "",
  company: "",
  phone: "",
};

const requiredFields = [
  "first_name",
  "last_name",
  "address1",
  "city",
  "zip",
  "province",
  "country",
  "province_code",
  "country_code",
];

const initialOrderData = {
  order_id: "",
  shipping_method: "",
  shipping_address: { ...initialAddress },
  billing_address: { ...initialAddress },
  customer: {
    name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: { ...initialAddress },
  },
  line_items: [
    {
      name: "",
      price: "",
      quantity: 1,
      sku: "",
      title: "",
      product_id: "",
      variant_id: "",
    },
  ],
};

const ManageOrder = () => {
  const [order, setOrder] = useState(initialOrderData);
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleFieldChange = (type, field, value) => {
    setOrder((prevOrder) => {
      const updatedOrder = { ...prevOrder };
      updatedOrder[`${type}_address`][field] = value;
      if (sameAsShipping && type === "shipping") {
        updatedOrder.billing_address[field] = value;
      }
      return updatedOrder;
    });
  };

  const handleCountryChange = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: "",
        province_code: "",
        country: value?.name || "",
        country_code: value?.code2 || "",
      },
    });
  };
  const handleProvinceSelect = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: value?.name || "",
        province_code: value?.code || "",
        country: value?.country?.name || "",
        country_code: value?.country?.code2 || "",
      },
    });
  };

  const handleProvinceChange = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: value?.name || "",
        province_code: value?.code || "",
      },
    });
  };

  const handleCheckboxChange = () => {
    setSameAsShipping(!sameAsShipping);
    setOrder((prevOrder) => ({
      ...prevOrder,
      billing_address: !sameAsShipping
        ? { ...prevOrder.shipping_address }
        : { ...initialAddress },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!order.shipping_address[field]) {
        newErrors[`shipping_${field}`] = "This field is required";
      }
      if (!sameAsShipping && !order.billing_address[field]) {
        newErrors[`billing_${field}`] = "This field is required";
      }
    });

    if (!order.order_id) {
      newErrors.order_id = "Order ID is required";
    }

    if (!order.shipping_method) {
      newErrors.shipping_method = "Shipping Method is required";
    }

    order.line_items.forEach((item, index) => {
      if (!item.product) {
        newErrors[`product_${index}`] = "Product is required";
      }
      if (!item.variant) {
        newErrors[`variant_${index}`] = "Variant is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      const orderData = {
        ...order,
        shipping_address: {
          ...order.shipping_address,
          name: `${order.shipping_address.first_name} ${order.shipping_address.last_name}`,
        },
        billing_address: {
          ...order.billing_address,
          name: `${order.billing_address.first_name} ${order.billing_address.last_name}`,
        },
        line_items: order.line_items.map((item) => ({
          name: item.product?.name,
          price: item.variant.price,
          quantity: item.quantity,
          sku: item.variant.sku,
          variant_title: item.variant.title,
          title: item.product?.name,
          product_id: item.product?._id,
          variant_id: item.variant?._id,
          type: "M2C",
        })),
        order_date: new Date().toISOString(),
      };
      try {
        const response = await orderService.create(orderData);
        console.log(response);
        resetAndBackToList();
      } catch (error) {
        console.error(error);
      }

      // Here you would typically send this data to your backend
    }
  };

  const resetAndBackToList = () => {
    setOrder(initialOrderData);
    setSameAsShipping(false);
    setErrors({});
    navigate("/orders");
  };

  const updateOrder = (value, field) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      [field]: value,
    }));
  };

  const updateQuantity = (value, index) => {
    setOrder((prevOrder) => {
      const updatedItems = [...prevOrder.line_items];
      updatedItems[index].quantity = value;
      return { ...prevOrder, line_items: updatedItems };
    });
  };
  const handleCustomerChange = (e) => {
    if (e) {
      setOrder((prevOrder) => ({
        ...prevOrder,
        customer: e,
        shipping_address: {
          first_name: e.first_name || "",
          last_name: e.last_name || "",
          address1: e.address.address1 || "",
          address2: e.address.address2 || "",
          company: e.address.company || "",
          city: e.address.city || "",
          zip: e.address.zip || "",
          province: e.address.province || "",
          province_code: e.address.province_code || e.address.province || "",
          country: e.address.country || "",
          country_code: e.address.country_code || e.address.country || "",
          phone: e.phone || "",
        },
      }));
    } else {
      setOrder((prevOrder) => ({
        ...prevOrder,
        customer: null,
        shipping_address: { ...initialAddress },
      }));
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        maxHeight: "98%",
      }}
    >
      <Typography sx={{ paddingBottom: 1 }} variant="h5" className="pb-4">
        Create Order
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "8px",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            Order ID <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            size="small"
            required={true}
            label={"Order ID"}
            fullWidth
            value={order.order_id}
            onChange={(e) => updateOrder(e.target.value, "order_id")}
            error={!!errors.order_id}
            helperText={errors.order_id}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "8px",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            Cutsomer <span style={{ color: "red" }}>*</span>
          </Typography>
          <CustomerAutocomplete
            selectedCustomer={order.customer}
            onCustomerChange={(e) => handleCustomerChange(e)}
            errorMessage={errors.customer}
            required={true}
          ></CustomerAutocomplete>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "8px",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            Shipping Method
          </Typography>
          <TextField
            size="small"
            required={true}
            label={"Shipping Method"}
            fullWidth
            value={order.shipping_method}
            onChange={(e) => updateOrder(e.target.value, "shipping_method")}
            error={!!errors.shipping_method}
            helperText={errors.shipping_method}
          />
        </Grid>
      </Grid>

      <Grid
        spacing={2}
        mt={2}
        sx={{
          borderWidth: 2,
          borderColor: "#D9D9D9",
          borderStyle: "solid",
          padding: "10px",
          borderRadius: 1,
        }}
      >
        {order.line_items.map((item, index) => (
          <Grid sx={{ padding: 1 }} container key={index}>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  textAlign: "left",
                  marginBottom: "2px",
                  fontSize: 14,
                }}
              >
                Product <span style={{ color: "red" }}>*</span>
              </Typography>
              <ProductSelector
                selectedProduct={item.product}
                onProductSelect={(product) => {
                  const updatedItems = [...order.line_items];
                  updatedItems[index] = {
                    ...updatedItems[index],
                    product,
                    variant: null,
                    quantity: 1, // Reset quantity to 1 when a new product is selected
                  };
                  setOrder({ ...order, line_items: updatedItems });
                }}
                errorMessage={errors[`product_${index}`]}
                required={true}
              />
            </Grid>
            <Grid sx={{ ml: 1 }} item xs={12} sm={4}>
              <FormControl
                size="small"
                fullWidth
                disabled={!item.product}
                error={!!errors[`variant_${index}`]}
              >
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    marginBottom: "8px",
                    textAlign: "left",
                    fontSize: 14,
                  }}
                >
                  Select Variant <span style={{ color: "red" }}>*</span>
                </Typography>
                <Select
                  label="Select Variant"
                  value={item.variant?._id || ""}
                  onChange={(event) => {
                    const variant = item.product.variants.find(
                      (v) => v._id === event.target.value
                    );
                    const updatedItems = [...order.line_items];
                    updatedItems[index] = { ...updatedItems[index], variant };
                    setOrder({ ...order, line_items: updatedItems });
                  }}
                >
                  {item.product?.variants.map((variant) => (
                    <MenuItem key={variant._id} value={variant._id}>
                      {variant.sku}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors[`variant_${index}`] || " "}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} display="flex" alignItems="center">
              <FormControl
                size="small"
                fullWidth
                error={!!errors[`quantity_${index}`]}
              >
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    marginBottom: "8px",
                    textAlign: "left",
                    fontSize: 14,
                  }}
                >
                  Quantity<span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  required={true}
                  disabled={!item.variant}
                  size="small"
                  value={item.quantity}
                  onChange={(e) => updateQuantity(e.target.value, index)}
                  type="number"
                  label="Quantity"
                  fullWidth
                  inputProps={{ min: 1 }}
                  sx={{ ml: 1 }}
                />
                <FormHelperText>
                  {errors[`quantity_${index}`] || " "}
                </FormHelperText>
              </FormControl>
              <IconButton
                disabled={order.line_items?.length === 1}
                onClick={() => {
                  setOrder({
                    ...order,
                    line_items: order.line_items.filter((_, i) => i !== index),
                  });
                }}
                size="small"
                sx={{ mb: 3, ml: 1 }}
              >
                <Remove />
              </IconButton>
              <IconButton
                onClick={() => {
                  setOrder({
                    ...order,
                    line_items: [
                      ...order.line_items,
                      {
                        name: "",
                        price: "",
                        quantity: 1,
                        sku: "",
                        product: null,
                        variant: null,
                      },
                    ],
                  });
                }}
                size="small"
                sx={{ mb: 3, ml: 1 }}
              >
                <AddIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Typography mt={1} variant="h6">
        Shipping Address
      </Typography>
      <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
        {Object.keys(fieldsToRender).map((field) => (
          <Grid item xs={12} md={4} key={field}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                marginBottom: "8px",
                textAlign: "left",
                fontSize: 14,
              }}
            >
              {field
                .replace(/_/g, " ")
                .replace(/([A-Z])/g, " $1")
                .trim()
                .replace(/^\w/, (c) => c.toUpperCase())}
              {requiredFields.includes(field) && (
                <span style={{ color: "red" }}>*</span>
              )}
            </Typography>
            <TextField
              size="small"
              required={requiredFields.includes(field)}
              label={field.replace(/([A-Z])/g, " $1").trim()}
              fullWidth
              value={
                order?.shipping_address[field]
                  ? order?.shipping_address[field]
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("shipping", field, e.target.value)
              }
              error={!!errors[`shipping_${field}`]}
              helperText={errors[`shipping_${field}`]}
            />
          </Grid>
        ))}
        <AddressLocationFields
          type="shipping_address"
          onCountryChange={(country) => {
            handleCountryChange("shipping_address", country);
          }}
          onProvinceSelect={(province) => {
            handleProvinceSelect("shipping_address", province);
          }}
          onProvinceChange={(province) => {
            handleProvinceChange("shipping_address", province);
          }}
          address={order.shipping_address}
          errors={errors}
          onZipChange={(zip) => {
            handleFieldChange("shipping", "zip", zip);
            setErrors((prevErrors) => ({
              ...prevErrors,
              shipping_zip: "",
            }));
          }}
          onCityChange={(city) => {
            handleFieldChange("shipping", "city", city);
            setErrors((prevErrors) => ({
              ...prevErrors,
              shipping_city: "",
            }));
          }}
        />
      </Grid>

      <FormControlLabel
        sx={{ marginTop: 1 }}
        control={
          <Checkbox checked={sameAsShipping} onChange={handleCheckboxChange} />
        }
        label="Billing Address is same as Shipping Address"
      />

      {!sameAsShipping && (
        <>
          <Typography variant="h6" className="mt-4">
            Billing Address
          </Typography>
          <Grid sx={{ marginTop: 1 }} container spacing={2} className="mt-2">
            {Object.keys(fieldsToRender).map((field) => (
              <Grid item xs={12} md={4} key={field}>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    marginBottom: "8px",
                    textAlign: "left",
                  }}
                >
                  {field
                    .replace(/_/g, " ")
                    .replace(/([A-Z])/g, " $1")
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase())}
                  {requiredFields.includes(field) && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </Typography>
                <TextField
                  size="small"
                  required={requiredFields.includes(field)}
                  label={field.replace(/([A-Z])/g, " $1").trim()}
                  fullWidth
                  value={order.billing_address[field]}
                  onChange={(e) =>
                    handleFieldChange("billing", field, e.target.value)
                  }
                  error={!!errors[`billing_${field}`]}
                  helperText={errors[`billing_${field}`]}
                />
              </Grid>
            ))}
            <AddressLocationFields
              type="billing_address"
              onCountryChange={(country) => {
                handleCountryChange("billing_address", country);
              }}
              onProvinceSelect={(province) => {
                handleProvinceSelect("billing_address", province);
              }}
              onProvinceChange={(province) => {
                handleProvinceChange("billing_address", province);
              }}
              address={order.billing_address}
              errors={errors}
              onZipChange={(zip) => {
                handleFieldChange("billing", "zip", zip);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  billing_zip: "",
                }));
              }}
              onCityChange={(city) => {
                handleFieldChange("billing", "city", city);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  billing_city: "",
                }));
              }}
            />
          </Grid>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "end" }}>
        {order.line_items.length > 0 && (
          <Typography variant="h6" sx={{ marginTop: 2, fontWeight: "bold" }}>
            Order Total: $
            {order.line_items.reduce(
              (total, item) => total + (item.variant?.price || 0),
              0
            )}
          </Typography>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => resetAndBackToList()}
          variant="contained"
          color="primary"
          sx={{ marginTop: 2, backgroundColor: "red", marginRight: 1 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ marginTop: 2 }}
        >
          Place Order
        </Button>
      </div>
    </div>
  );
};

export default ManageOrder;
